import { useAuth0 } from '@auth0/auth0-react';
import JSON5 from 'json5';
import React, {
  createContext,
  ReactNode,
  useMemo,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { ReadyState, SendMessage } from 'react-use-websocket';

import { getSessionTracker, TRACKER_EVENT_KEYS } from '../../hooks/use-session-tracker';
import { ConnectionErrorVariant, GlobalContext } from '../global-provider';

import { handleNewMessage, ParsedWebSocketMessage } from './handle-messages';
import {
  subscribeToChannel,
  unsubscribeFromChannel,
  unsubscribeAllChannelsInTabOverview,
  unsubscribeByWidget,
} from './handle-subscriptions';
import { useHeartbeat } from './use-hearbeat';
import { useWebSocketConnection } from './use-websocket-connection';
import AuthContext from '../auth/auth-service';

interface IWebsocketProvider {
  subscribeToChannel: any;
  unsubscribeFromChannel: any;
  unsubscribeAllChannelsInTabOverview: any;
  unsubscribeByWidget: any;
  isConnected: boolean;
  isReconnection: boolean;
  status: string;
  statusEnum: ReadyState;
  sendMessage: SendMessage;
}

export const STATES = ReadyState;

export const WebSocketContext = createContext<IWebsocketProvider>({} as IWebsocketProvider);

const BASE_URL = import.meta.env.REACT_APP_WEBSOCKET_URL;
const MAX_TIME_TO_REQUIRE_USER_TO_RELOAD_PAGE = 30000; // 30 seconds
const LATENCY_STALE_TIMEOUT = 30000; // 30 seconds
const PENDING_REQUEST_CLEANUP_INTERVAL = 10000; // 10 seconds
const FAST_WS_LATENCY_THRESHOLD = 400;
const MEDIUM_WS_LATENCY_THRESHOLD = 1500;
const MAX_PENDING_REQUESTS_PER_ID = 5;

type PendingRequest = { timestamp: number; method: string };

type PendingRequestsById = Map<string, PendingRequest[]>;

/**
 * IM-1991 fix #3: the WS URL carries the Immersive JWT as a query param. It must
 * be rebuilt whenever the token changes (after a refresh re-exchange or a
 * reauth) so the socket reconnects with the FRESH token, and must be null while
 * unauthenticated or before a token exists so we never open with an empty token.
 */
export function buildWsUrl(
  baseUrl: string,
  isAuthenticated: boolean,
  token: string | null,
): string | null {
  if (!isAuthenticated || !token) {
    return null;
  }
  return `${baseUrl}/ws?token=${token}`;
}

export function classifyLatency(duration: number): 'fast' | 'medium' | 'slow' {
  if (duration < FAST_WS_LATENCY_THRESHOLD) return 'fast';
  if (duration <= MEDIUM_WS_LATENCY_THRESHOLD) return 'medium';
  return 'slow';
}

export function buildWsLatencyEventLabel(category: ReturnType<typeof classifyLatency>) {
  return `${TRACKER_EVENT_KEYS.WS_LATENCY}_${category}`;
}

export function registerPendingRequest(
  pendingRequests: PendingRequestsById,
  id: string,
  entry: PendingRequest,
) {
  const queue = pendingRequests.get(id);

  if (queue) {
    if (queue.length >= MAX_PENDING_REQUESTS_PER_ID) {
      queue.shift();
    }

    queue.push(entry);
    return;
  }

  pendingRequests.set(id, [entry]);
}

export function consumePendingRequest(pendingRequests: PendingRequestsById, id: string) {
  const queue = pendingRequests.get(id);

  if (!queue?.length) {
    return null;
  }

  const entry = queue.shift() ?? null;

  if (queue.length === 0) {
    pendingRequests.delete(id);
  }

  return entry;
}

export function cleanStalePendingRequests(pendingRequests: PendingRequestsById) {
  const now = performance.now();

  for (const [id, queue] of pendingRequests) {
    const nextQueue = queue.filter((entry) => now - entry.timestamp <= LATENCY_STALE_TIMEOUT);

    if (nextQueue.length === 0) {
      pendingRequests.delete(id);
      continue;
    }

    if (nextQueue.length !== queue.length) {
      pendingRequests.set(id, nextQueue);
    }
  }
}

export const WebSocketProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth0();
  const {
    activeChannelsRef,
    messageHandlers,
    marketDataOverviewCurrentToken: currency,
    setShowReloadMessage,
    showReloadMessage,
    setConnectionErrorVariant,
    onUnsubscribeHandlers,
  } = useContext(GlobalContext);

  const [isReconnection, setIsReconnection] = useState(false);

  const pendingRequestsRef = useRef<PendingRequestsById>(new Map());
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasEverConnectedRef = useRef(false);

  const { accessToken: token } = useContext(AuthContext);

  // A permissions 401 rejects the WS upgrade before it ever opens; a genuine
  // network drop opens the socket first, then loses it. The browser never
  // exposes the upgrade status to JS, so "authenticated with a JWT but the
  // socket has never opened" is our honest discriminator for an access denial.
  const resolveConnectionErrorVariant = useCallback(
    (): ConnectionErrorVariant =>
      isAuthenticated && !!token && !hasEverConnectedRef.current ? 'access' : 'network',
    [isAuthenticated, token],
  );

  const [socketUrl, setSocketUrl] = useState<string | null>(
    buildWsUrl(BASE_URL, isAuthenticated, token),
  );

  // Rebuild the socket URL when auth state or the Immersive JWT changes. A new
  // token yields a new URL, which forces react-use-websocket to reconnect with
  // the fresh JWT (IM-1991 fix #3). Keyed strictly on [isAuthenticated, token]
  // so it never fights the disconnect-timeout effect below (token unchanged
  // there).
  useEffect(() => {
    setSocketUrl(buildWsUrl(BASE_URL, isAuthenticated, token));
  }, [isAuthenticated, token]);

  const { sendMessage: rawSendMessage, readyState } = useWebSocketConnection(
    socketUrl,
    resolveConnectionErrorVariant,
    (message) => {
      let data: ParsedWebSocketMessage = null;

      try {
        data = JSON5.parse(message.data);

        if (data?.id) {
          const entry = consumePendingRequest(pendingRequestsRef.current, data.id);

          if (entry) {
            const duration = performance.now() - entry.timestamp;
            const category = classifyLatency(duration);

            try {
              const { tracker } = getSessionTracker();
              if (tracker) {
                tracker.event(
                  buildWsLatencyEventLabel(category),
                  JSON.stringify({
                    method: entry.method,
                    duration: Math.round(duration),
                    category,
                  }),
                );
              }
            } catch (err) {
              console.error('Error sending ws_latency event:', err);
            }
          }
        }
      } catch (error) {
        console.error(error);
        return;
      }

      handleNewMessage(data, {
        currency,
        isReconnection,
        setIsReconnection,
        activeChannels: activeChannelsRef.current,
        messageHandlers: messageHandlers.current,
        resetHeartbeatTimeout,
        onUnsubscribeHandlers: onUnsubscribeHandlers.current,
      });
    },
  );

  const sendMessage: SendMessage = useCallback(
    (message, keep?) => {
      try {
        const parsed = typeof message === 'string' ? JSON.parse(message) : null;
        if (
          parsed?.id &&
          parsed?.method &&
          parsed.id !== 'CUSTOM_HELLO_MESSAGE' &&
          !String(parsed.method).startsWith('subscribe.') &&
          !String(parsed.method).startsWith('unsubscribe.')
        ) {
          registerPendingRequest(pendingRequestsRef.current, parsed.id, {
            timestamp: performance.now(),
            method: parsed.method,
          });
        }
      } catch {
        // not JSON — send as-is
      }
      rawSendMessage(message, keep);
    },
    [rawSendMessage],
  );

  const isConnectionOpen = readyState === ReadyState.OPEN;
  const isConnectionClosed = readyState === ReadyState.CLOSED;

  const handleConnectionLost = () => {
    setIsReconnection(true);
  };

  const { resetHeartbeatTimeout } = useHeartbeat({
    sendMessage,
    onConnectionLost: handleConnectionLost,
    isConnectionOpen,
  });

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      hasEverConnectedRef.current = true;
    }
  }, [readyState]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      cleanStalePendingRequests(pendingRequestsRef.current);
    }, PENDING_REQUEST_CLEANUP_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (isReconnection || isConnectionClosed) {
      pendingRequestsRef.current.clear();
      disconnectTimeoutRef.current = setTimeout(() => {
        setSocketUrl(null);
        setConnectionErrorVariant(resolveConnectionErrorVariant());
        setShowReloadMessage(true);
      }, MAX_TIME_TO_REQUIRE_USER_TO_RELOAD_PAGE);
    } else {
      if (disconnectTimeoutRef.current && socketUrl) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
      if (showReloadMessage) setShowReloadMessage(false);
    }

    return () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
    };
  }, [isReconnection, isConnectionClosed]);

  const status: string = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  const value = useMemo(() => {
    return {
      isConnected: isConnectionOpen,
      isReconnection,
      status,
      statusEnum: readyState,
      subscribeToChannel: subscribeToChannel(messageHandlers, onUnsubscribeHandlers),
      unsubscribeFromChannel: unsubscribeFromChannel(
        sendMessage,
        activeChannelsRef,
        messageHandlers,
      ),
      unsubscribeAllChannelsInTabOverview: unsubscribeAllChannelsInTabOverview(
        sendMessage,
        activeChannelsRef,
        messageHandlers,
      ),
      unsubscribeByWidget: unsubscribeByWidget(sendMessage, activeChannelsRef, messageHandlers),
      sendMessage,
    };
  }, [readyState, isReconnection]);

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
